export interface IJobLocations {
  id: string;
  name: string;
  parent: string;
}

export type TJobMode = "Remote" | "WFO" | "Hybrid";
export interface IExperienceRange {
  min: string;
  max: string;
}

export interface ISalaryRange {
  min: string;
  max: string;
  currency: string;
}

export interface ICampaignSetupSlice {
  jobTitle: string;
  jobLocations: IJobLocations[];
  jobMode: TJobMode;
  experienceRange: IExperienceRange;
  salaryRange: ISalaryRange;
  campaignName: string;
  selectedFolder: IFolderDetails | null;
  numberOfSelectedCandidates: string;
  isSendToAlreadyContacted: boolean | null;
  noticePeriod: string;
  isSalaryRangeFilledInSetup: boolean;
}

export enum EMediaType {
  TEXT = "text",
  DOCUMENT = "document",
  IMAGE = "image",
}
export interface IMediaDetails {
  addMediaFlag: boolean;
  mediaType: EMediaType;
  uploadedFile: File | null;
  mediaFileName: string;
  mediaFileSize: string;
}

export interface ITemplate {
  id: string;
  content: string;
  corpId: number;
  name: string;
  subuid: number;
  type: EMediaType;
  vendorTemplateId: string;
  optionalPlaceholders: string[] | null;
}

export interface ITemplatePlaceholderValues {
  [placeholder: string]: string;
}

export interface ITemplatePlaceholders {
  [placeholder: string]: string | ITemplate;
  template: ITemplate;
}

export interface ICampaignSenderDetails {
  number: string;
  name: string;
  profilePic: string;
  email: string;
  id: string;
  countryCode: string;
}

export type TNoticePeriod = "days" | "months";
export type TApiStatus = "idle" | "pending" | "success" | "error";
export interface ICampaignComposeSlice {
  mediaDetails: IMediaDetails;
  campaignSenderDetails: ICampaignSenderDetails;
  templateDetails: {
    templates: ITemplate[];
    selectedTemplate: ITemplate | null;
    fetchStatus: TApiStatus;
  };
  templatePlaceholders: ITemplatePlaceholderValues;
  templatesPlaceholderCache: {
    [templateId: string]: {
      placeholders: ITemplatePlaceholderValues;
      template: ITemplate;
    };
  };
}

export interface ICampaignContactableDetails {
  contactableCount: number | null;
  folderId: number | null;
  notContactableCount: number | null;
  totalCount: number | null;
  profileCount: number;
}

export interface ICampaignInitialDetails {
  jobLocations: string[] | null;
  jobTitle: string | null;
  maxExp: string | null;
  maxSalary: number | null;
  minExp: string | null;
  minSalary: number | null;
  currency: string | null;
}

export interface ICampaignDraftDetails {
  campaignDetail: {
    id: number;
    name: string;
    subuid: number;
    corpId: string;
    folderId: number;
    status: string;
    campaignEmail: string;
    triggeredFromPage: string;
    isBulkCampaign: boolean;
    stepsCount: number;
    receiveReplyOnEmail: string;
    nextStep: number;
    channelId: number;
    subChannelId: number;
    campaignInitiationDetailsId: number;
    createdAt: string;
    updatedAt: string;
    isTemplateUsed: boolean;
    selectedCandidatesCount: number;
    sendMailToContactedProfiles: number;
    campaignType: string;
    pausedBy: string | null;
    finishedAt: string;
    passiveJob: number;
    osType: number;
  };
  campaignJobDescription: {
    id: string;
    campaignId: number;
    jobTitle: string;
    jobLocations: string[];
    minSalary: number;
    maxSalary: number;
    currencyCode: string;
    minExp: string;
    maxExp: string;
    jobMode: TJobMode;
    folderId: string;
    campaignName: string;
    selectedCandidates: number;
    noticePeriod: string;
    disabledFields: string[];
    channelName: string | null;
  };
  whatsAppDetails: {
    id: string;
    templateId: string;
  };
}

export interface IFaqDetails {
  id: string;
  body: string;
  title: string;
}

export interface ICurrencyList {
  id: string;
  name: string;
  sign: string;
  code?: string;
}

export interface ISaveAndContinueResponse {
  id: string;
}

export type TRequestStatus = "idle" | "pending" | "success" | "error";

export interface IWhatsAppCampaign {
  initialDetails: ICampaignInitialDetails;
  contactableDetails: ICampaignContactableDetails;
  draftDetails: ICampaignDraftDetails | null;
  faqDetails: IFaqDetails[];
  jobLocations: ILocationResponse | null;
  currency: ICurrencyList[];
  saveAndContinueResponse: ISaveAndContinueResponse | null;
  folders: IFolderDetails[];
  fetchFoldersStatus: TRequestStatus;
}

export interface IJobTitleSuggestions {
  value: string;
  label: string;
}

export interface IFolderDetails {
  folderId: number;
  folder_created: string;
  name: string;
  num_profiles: number;
  type: string;
}

export interface ICitizenShip {
  code: string;
  dl: string;
  id: string;
  dId: string;
}

export interface ILocationResponse {
  topCities: { id: number; value: string }[];
  regionAndStates: {
    id: number;
    name: string;
    cities: { id: number; value: string }[];
  }[];
  internationalLocations: { id: number; value: string }[];
  currency: ICurrencyList[];
  citizenship: ICitizenShip[];
}

export interface ICampaignSaveAsDraftPayload {
  campaignId: number | null;
  campaignName: string;
  folderId: number | null;
  folderName: string;
  initiationId: number | null;

  jobDescription: {
    currencyCode: string;
    jobLocations: string[];
    jobTitle: string;
    jobMode: TJobMode;
    minExp: string;
    maxExp: string;
    maxSalary: string;
    minSalary: string;
    noticePeriod?: string | null;
    disabledFields?: string[];
    channelName?: string | null;
  };
  recruiterDetail: null;
  selectedCount: number;
  sendMailToContactedProfiles: number;
  templateId?: string | null;
  whatsAppCountryCode?: string;
  whatsAppName?: string;
  whatsAppNumber?: string;
  whatsAppNumberId?: string;
  status: "DRAFT" | "SCHEDULED";
}

export interface ICampaignStartCampaignPayload extends ICampaignSaveAsDraftPayload {
  file: File | null;
}

export interface IErrors {
  jobTitle?: string;
  jobLocations?: string;
  experienceRange?: string;
  campaignName?: string;
  selectCandidates?: string;
  saveInFolder?: string;
  addMedia?: string;
  salaryRange?: string;
}

export interface IInventoryUsageData {
  totalLimit: number | null;
  totalConsumed: number | null;
  monthlyLimit: number;
  monthlyConsumed: number;
  dailyLimit: number;
  dailyConsumed: number;
  unlimited: number;
  totalServiceLimit: number;
  totalServiceConsumed: number;
  enabled: number;
  corpInvId: number;
  level: string; // "recruiter"|"corp",
  isLimitReached: boolean;
  isExhausted: boolean;
  inventoryLevel: string; // daily, monthly, total
}

export interface OutreachBannerConfig {
  level: string;// "recruiter" | "corp"
  isLimitReached: boolean;
  isExhausted: boolean;
  inventoryLevel: string; //"daily" | "monthly" | "total"
}
